// Path: src/hooks/useAppVersion.ts

import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../store/store";

export const useAppVersion = () => {
  const activeSession = useSelector((state: RootState) => state.activeSession.session);
  const isInConsultation = !!activeSession;

  const currentVersion = useRef<string | null>(null);
  const dismissedVersion = useRef<string | null>(null);

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [deployedAt, setDeployedAt] = useState<string | null>(null);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`);
        if (!res.ok) return;

        const data = await res.json();
        const incoming = data.version as string;
      

        // First load — just store the version, don't show banner
        if (!currentVersion.current) {
          currentVersion.current = incoming;
          return;
        }

        // Same version — nothing to do
        if (incoming === currentVersion.current) return;

        // User already dismissed this exact version — don't nag again
        if (incoming === dismissedVersion.current) return;

        // New version detected — store it
        setNewVersion(incoming);
        setDeployedAt(data.deployedAt ?? null);

        // Only show banner if user is NOT in a paid consultation
        if (!isInConsultation) {
          setUpdateAvailable(true);
        }

      } catch {
        // Network error — silently ignore
      }
    };

    check();
    const interval = setInterval(check, 60000); // poll every 60 seconds
    return () => clearInterval(interval);
  }, [isInConsultation]);

  // When consultation ends, show pending update banner if one exists
  useEffect(() => {
    if (!isInConsultation && newVersion && newVersion !== currentVersion.current) {
      if (newVersion !== dismissedVersion.current) {
        setUpdateAvailable(true);
      }
    }
  }, [isInConsultation, newVersion]);

  const dismiss = () => {
    dismissedVersion.current = newVersion;
    setUpdateAvailable(false);
  };

  const refresh = () => {
    window.location.assign(window.location.href); // true navigation, not cached reload
  };

  // Format deployedAt into readable time e.g. "3:30 PM"
  const formattedTime = deployedAt
    ? new Date(deployedAt).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : null;

  return { updateAvailable, formattedTime, dismiss, refresh };
};